import type { Outcome } from '@/store/commit'
import type { AppDispatch } from '@/store/create-store'
import type { RootState } from '@/store/root-reducer'

import { createUser, deleteUser, updateUser, type CreateUserInput, type CreateUserResult, type UpdateUserInput } from './commands'
import { normalizeName } from './normalize'
import { selectAllAccounts } from './selectors'
import type { Account, User } from './types'

export interface AccountSearchRequest {
  type: 'SUPPLIER' | 'CLIENT'
  query: string
}

export interface AccountMatch {
  account: Account
}

export interface AccountSearchResult {
  /** Accounts of the requested type whose name matches the query. */
  matches: AccountMatch[]
}

/**
 * Everything the Users screen needs from a backend. Writes go to the API; searching reads the accounts already loaded
 * from it (the API has no name search, only a filter by type).
 */
export interface AccountsRepository {
  /** Rejects with an `Error` when the lookup itself fails: callers must NOT treat that as "no match". */
  searchAccounts(request: AccountSearchRequest, options?: { signal?: AbortSignal }): Promise<AccountSearchResult>
  /** For the new-account modes this creates the account and its first user. */
  createUser(input: CreateUserInput): Promise<Outcome<CreateUserResult>>
  updateUser(input: UpdateUserInput): Promise<Outcome<User>>
  deleteUser(userId: string): Promise<Outcome<User>>
}

export function createAccountsRepository(store: { getState: () => RootState; dispatch: AppDispatch }): AccountsRepository {
  return {
    async searchAccounts({ type, query }, options) {
      if (options?.signal?.aborted) throw new DOMException('The lookup was cancelled.', 'AbortError')
      const wanted = normalizeName(query)
      const matches = selectAllAccounts(store.getState())
        .filter((account) => account.accountType === type && normalizeName(account.accountName).includes(wanted))
        .map((account) => ({ account }))
      return { matches }
    },
    createUser: (input) => store.dispatch(createUser(input)),
    updateUser: (input) => store.dispatch(updateUser(input)),
    deleteUser: (userId) => store.dispatch(deleteUser(userId)),
  }
}
